import { DateRange } from '@mui/x-date-pickers-pro';
import { format } from 'date-fns';
import { checkBoundary } from './common';

const DEFAULT_TIME_ZONE = 'UTC';

// Some browsers/WebViews (notably certain Android WebView versions) expose `Intl.DateTimeFormat`
// but not `Intl.DateTimeFormat.supportedLocalesOf`, which the constructor relies on internally
// and throws when missing. Guard against that instead of crashing.
const getLocalTimeZone = () => {
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat?.supportedLocalesOf !== 'function') {
        return DEFAULT_TIME_ZONE;
    }

    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? DEFAULT_TIME_ZONE;
    } catch {
        return DEFAULT_TIME_ZONE;
    }
};

const copyDate = (fromDate: Date, toDate: Date) => {
    const newDate = new Date(toDate.getTime());
    newDate.setDate(fromDate.getDate());
    newDate.setMonth(fromDate.getMonth());
    newDate.setFullYear(fromDate.getFullYear());

    return newDate;
};

const copyTime = (fromDate: Date, toDate: Date) => {
    const newDate = new Date(toDate.getTime());
    newDate.setHours(fromDate.getHours(), fromDate.getMinutes(), fromDate.getSeconds(), fromDate.getMilliseconds());
    return newDate;
};

const validateRange = (
    value: DateRange<Date>,
    setError: (msg: string | null) => void,
    minDate: Date | null = null,
    maxDate: Date | null = null,
    allowIncompleteValues = true
) => {
    const [start, end] = value;

    if (!allowIncompleteValues && (!start || !end)) {
        return false;
    }

    if (start && end) {
        if (start > end) {
            setError('Start date cannot be after end date');
            return false;
        }

        if (
            minDate &&
            (checkBoundary(start, minDate, (d, b) => d < b) || checkBoundary(end, minDate, (d, b) => d < b))
        ) {
            setError(`Dates cannot be prior to ${format(minDate, 'yyyy-MM-dd')}`);
            return false;
        }

        if (
            maxDate &&
            (checkBoundary(start, maxDate, (d, b) => d > b) || checkBoundary(end, maxDate, (d, b) => d > b))
        ) {
            setError(`Dates cannot be after ${format(maxDate, 'yyyy-MM-dd')}`);
            return false;
        }
    } else {
        if (start) {
            if (minDate && checkBoundary(start, minDate, (d, b) => d < b)) {
                setError(`Start date cannot be prior to ${format(minDate, 'yyyy-MM-dd')}`);
                return false;
            }
            if (maxDate && checkBoundary(start, maxDate, (d, b) => d > b)) {
                setError(`Start date cannot be after ${format(maxDate, 'yyyy-MM-dd')}`);
                return false;
            }
        }

        if (end) {
            if (minDate && checkBoundary(end, minDate, (d, b) => d < b)) {
                setError(`End date cannot be prior to ${format(minDate, 'yyyy-MM-dd')}`);
                return false;
            }
            if (maxDate && checkBoundary(end, maxDate, (d, b) => d > b)) {
                setError(`End date cannot be after ${format(maxDate, 'yyyy-MM-dd')}`);
                return false;
            }
        }
    }

    setError(null);
    return true;
};

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'always' });

const formatTimeAgo = (dateString?: string | null) => {
    if (!dateString) {
        return '-';
    }

    const timestamp = new Date(dateString).getTime();
    if (Number.isNaN(timestamp)) {
        return '-';
    }

    const diffInSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));

    if (diffInSeconds < 60) return relativeTimeFormatter.format(-diffInSeconds, 'second');

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return relativeTimeFormatter.format(-diffInMinutes, 'minute');

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return relativeTimeFormatter.format(-diffInHours, 'hour');

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return relativeTimeFormatter.format(-diffInDays, 'day');

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 5) return relativeTimeFormatter.format(-diffInWeeks, 'week');

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) return relativeTimeFormatter.format(-diffInMonths, 'month');

    const diffInYears = Math.floor(diffInDays / 365);
    return relativeTimeFormatter.format(-diffInYears, 'year');
};

export { copyDate, copyTime, formatTimeAgo, getLocalTimeZone, validateRange };
